import React from 'react';
import {
  CalendarClock,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  DollarSign,
  FileText,
  History,
  MapPin,
  Scale,
  Shield,
  Truck,
  UserCircle,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import FieldClockAction from '../../components/field/FieldClockAction';
import FieldTileGrid, { type FieldTile } from '../../components/field/FieldTileGrid';
import SignOutButton from '../../components/SignOutButton';

/**
 * THE FIELD HOME FOR TRUCKING DRIVERS AND RENTAL TECHS (fix/field-home-trucking-rentals).
 *
 * Casing's field home, in shape and in parts: the clock card on top, a
 * two-column tile grid, no bottom tab bar (fe#560 / fe#562). The driver app had
 * three different bottom bars depending on the screen (Today / Schedule /
 * Forms / History / Profile on /my-work; Home / Clock In / My Jobs / Safety /
 * More on a load, linking to casing routes this tree does not have; Loads /
 * BOLs / + / Safety / Profile on the BOL list). This one home replaces all
 * three, and every screen a tile opens has a way back to it.
 *
 * The tiles are the operator's lists, each behind a positive vertical guard:
 *   driver      Today's loads, BOLs, Weight ticket, Trip inspection, Time off,
 *               Safety, My pay, Profile
 *   rental tech Today's dispatches, Yard check-out/in, Inspections, Time off,
 *               Safety, Profile
 * A tenant with both gets one "Today's work" tile and the union, each
 * destination once.
 *
 * Schedule, History and Forms had a tab, then sat under the grid as plain
 * text links; they are tiles like the rest (fe#815). Forms is casing's tile
 * word for word (FieldHomeScreen), and sits after Safety as it does there.
 * Casing has no Schedule or History tile, so those two are written in the
 * same shape: Schedule after Today, History before Profile.
 */

const TODAY_ICON = Truck;

export function tilesFor(trucking: boolean, rentals: boolean): FieldTile[] {
  const today: FieldTile = {
    title: trucking && rentals ? "Today's work" : trucking ? "Today's loads" : "Today's dispatches",
    subtitle: trucking && rentals ? 'Loads, deliveries, returns' : trucking ? 'Assigned & in progress' : 'Deliveries & returns',
    icon: TODAY_ICON,
    path: '/my-work/today',
    color: 'text-fieldiq-accent',
  };
  const driver: FieldTile[] = [
    { title: 'BOLs', subtitle: 'Your bills of lading', icon: FileText, path: '/driver/bols', color: 'text-amber-400' },
    { title: 'Weight ticket', subtitle: 'File a scale ticket', icon: Scale, path: '/driver/weight-ticket/new', color: 'text-sky-400' },
    { title: 'Trip inspection', subtitle: 'Pre-trip & post-trip', icon: ClipboardCheck, path: '/field/trip-inspections', color: 'text-sky-400' },
  ];
  const tech: FieldTile[] = [
    { title: 'Yard check-out/in', subtitle: 'Send out & take back', icon: MapPin, path: '/rentals/yard', color: 'text-amber-400' },
    { title: 'Inspections', subtitle: 'Delivery & return checks', icon: ClipboardCheck, path: '/rentals/inspections', color: 'text-sky-400' },
  ];
  const shared: FieldTile[] = [
    { title: 'Time off', subtitle: 'Request & view', icon: CalendarDays, path: '/field/time-off', color: 'text-cyan-400' },
    { title: 'Safety', subtitle: 'Meetings, JSAs & incidents', icon: Shield, path: '/field/safety', color: 'text-red-400' },
  ];
  const schedule: FieldTile = { title: 'Schedule', subtitle: 'Upcoming work', icon: CalendarClock, path: '/my-work/schedule', color: 'text-fieldiq-accent' };
  const forms: FieldTile = { title: 'Forms', subtitle: 'Safety & inspection forms', icon: ClipboardList, path: '/my-work/forms', color: 'text-sky-400' };
  const history: FieldTile = { title: 'History', subtitle: 'Your past work', icon: History, path: '/my-work/history', color: 'text-purple-400' };
  const pay: FieldTile = { title: 'My pay', subtitle: 'Periods & earnings', icon: DollarSign, path: '/field/pay', color: 'text-amber-400' };
  const profile: FieldTile = { title: 'Profile', subtitle: 'Your info & certs', icon: UserCircle, path: '/my-work/profile', color: 'text-blue-400' };

  return [
    today,
    schedule,
    ...(trucking ? driver : []),
    ...(rentals ? tech : []),
    ...shared,
    forms,
    ...(trucking ? [pay] : []),
    history,
    profile,
  ];
}

export default function FieldHomePage() {
  const { hasVertical } = useAuth();
  const tiles = tilesFor(hasVertical('trucking'), hasVertical('surface_rentals'));

  return (
    <div data-testid="field-home" className="space-y-6">
      <FieldClockAction />
      <FieldTileGrid tiles={tiles} />
      <SignOutButton />
    </div>
  );
}
